import type { MutableRefObject } from 'react';

import type { BoardColumn } from '../interfaces/BoardDetails';
import type { Idea } from '../interfaces/Idea';
import type { RankBy } from '../types/boardCardOrdering';

export type RandomOrderRef = MutableRefObject<Map<string, number>>;

export const getAddedTimestamp = (added: string) => {
  const timestamp = Date.parse(added);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getBoardSortNo = (idea: Idea) =>
  typeof idea.sortNo === 'number' && Number.isFinite(idea.sortNo) && idea.sortNo > 0
    ? idea.sortNo
    : Number.MAX_SAFE_INTEGER;

const syncRandomOrder = (ideas: Idea[], randomOrderRef: RandomOrderRef) => {
  const visibleIdeaIds = new Set(ideas.map((idea) => idea.id));

  randomOrderRef.current.forEach((_value, ideaId) => {
    if (!visibleIdeaIds.has(ideaId)) {
      randomOrderRef.current.delete(ideaId);
    }
  });

  const missingIdeas = ideas.filter((idea) => !randomOrderRef.current.has(idea.id));
  if (missingIdeas.length === 0) {
    return;
  }

  if (randomOrderRef.current.size === 0) {
    missingIdeas.forEach((idea) => {
      randomOrderRef.current.set(idea.id, Math.random());
    });
    return;
  }

  const highestRandomOrder = Math.max(...Array.from(randomOrderRef.current.values()));
  [...missingIdeas]
    .sort((left, right) => getAddedTimestamp(left.suggested) - getAddedTimestamp(right.suggested))
    .forEach((idea, index) => {
      randomOrderRef.current.set(idea.id, highestRandomOrder + index + 1);
    });
};

export const getOrderedBoardIdeas = (
  ideas: Idea[],
  rankBy: RankBy,
  randomizeCardPosition: boolean,
  randomOrderRef?: RandomOrderRef
) => {
  if (rankBy === 'board' && randomizeCardPosition && randomOrderRef) {
    syncRandomOrder(ideas, randomOrderRef);
  }

  const getRankValue = (idea: Idea) => {
    if (rankBy === 'board') {
      if (randomizeCardPosition && randomOrderRef) {
        return randomOrderRef.current.get(idea.id) ?? Number.MAX_SAFE_INTEGER;
      }

      return getBoardSortNo(idea);
    }

    if (rankBy === 'votes') {
      return idea.votes.length;
    }
    if (rankBy === 'comments') {
      return idea.comments.length;
    }
    return getAddedTimestamp(idea.suggested);
  };

  return [...ideas].sort((left, right) => {
    const leftPinned = left.pinned === true;
    const rightPinned = right.pinned === true;

    if (leftPinned !== rightPinned) {
      return leftPinned ? -1 : 1;
    }

    const rankDiff =
      rankBy === 'board' || rankBy === 'added'
        ? getRankValue(left) - getRankValue(right)
        : getRankValue(right) - getRankValue(left);

    if (rankDiff !== 0) {
      return rankDiff;
    }

    return rankBy === 'board' || rankBy === 'votes' || rankBy === 'comments' || rankBy === 'added'
      ? getAddedTimestamp(left.suggested) - getAddedTimestamp(right.suggested)
      : getAddedTimestamp(right.suggested) - getAddedTimestamp(left.suggested);
  });
};

export const getCustomOrderedBoardIdeas = (ideas: Idea[]) =>
  [...ideas].sort((left, right) => {
    const leftSortNo = getBoardSortNo(left);
    const rightSortNo = getBoardSortNo(right);
    const sortDiff = leftSortNo - rightSortNo;

    if (sortDiff !== 0) {
      return sortDiff;
    }

    return getAddedTimestamp(left.suggested) - getAddedTimestamp(right.suggested);
  });

export const moveCardOrderItem = (
  cardOrder: string[],
  draggedCardId: string,
  targetCardId: string
) => {
  const draggedIndex = cardOrder.indexOf(draggedCardId);
  const targetIndex = cardOrder.indexOf(targetCardId);

  if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) {
    return cardOrder;
  }

  const nextCardOrder = [...cardOrder];
  nextCardOrder.splice(draggedIndex, 1);
  nextCardOrder.splice(targetIndex, 0, draggedCardId);
  return nextCardOrder;
};

const getResolvedColumnId = (idea: Idea, columns: BoardColumn[]) => {
  const firstColumnId = columns[0]?.id ?? '';
  const validColumnIds = new Set(columns.map((column) => column.id));

  return idea.columnId && validColumnIds.has(idea.columnId) ? idea.columnId : firstColumnId;
};

export const getCardOrderWithColumnOrder = ({
  orderedIdeas,
  columns,
  columnId,
  columnCardOrder,
}: {
  orderedIdeas: Idea[];
  columns: BoardColumn[];
  columnId: string;
  columnCardOrder: string[];
}) => {
  const visibleIdeasById = new Map(orderedIdeas.map((idea) => [idea.id, idea]));
  const orderedColumnIdeaIds = orderedIdeas
    .filter((idea) => getResolvedColumnId(idea, columns) === columnId)
    .map((idea) => idea.id);
  const orderedColumnIdeaIdSet = new Set(orderedColumnIdeaIds);
  const uniqueColumnCardOrder = Array.from(new Set(columnCardOrder)).filter((ideaId) => {
    const idea = visibleIdeasById.get(ideaId);

    return idea && getResolvedColumnId(idea, columns) === columnId;
  });
  const nextColumnCardOrder = [
    ...uniqueColumnCardOrder,
    ...orderedColumnIdeaIds.filter((ideaId) => !uniqueColumnCardOrder.includes(ideaId)),
  ].filter((ideaId) => orderedColumnIdeaIdSet.has(ideaId));

  if (nextColumnCardOrder.length === 0) {
    return orderedIdeas.map((idea) => idea.id);
  }

  let nextColumnCardIndex = 0;

  return orderedIdeas.map((idea) => {
    if (getResolvedColumnId(idea, columns) !== columnId) {
      return idea.id;
    }

    const nextColumnCardId = nextColumnCardOrder[nextColumnCardIndex];
    nextColumnCardIndex += 1;

    return nextColumnCardId ?? idea.id;
  });
};

export const getCardOrderWithCardMovedToColumnPosition = ({
  orderedIdeas,
  columns,
  draggedCardId,
  targetColumnId,
  targetCardId,
}: {
  orderedIdeas: Idea[];
  columns: BoardColumn[];
  draggedCardId: string;
  targetColumnId: string;
  targetCardId?: string;
}) => {
  if (!orderedIdeas.some((idea) => idea.id === draggedCardId)) {
    return orderedIdeas.map((idea) => idea.id);
  }

  const remainingIdeas = orderedIdeas.filter((idea) => idea.id !== draggedCardId);
  const targetColumnCardIds = remainingIdeas
    .filter((idea) => getResolvedColumnId(idea, columns) === targetColumnId)
    .map((idea) => idea.id);
  const targetIndex = targetCardId ? targetColumnCardIds.indexOf(targetCardId) : -1;
  const nextTargetColumnCardIds = [...targetColumnCardIds];

  if (targetIndex >= 0) {
    nextTargetColumnCardIds.splice(targetIndex, 0, draggedCardId);
  } else {
    nextTargetColumnCardIds.push(draggedCardId);
  }

  let nextTargetColumnCardIndex = 0;
  const nextCardOrder: string[] = [];

  remainingIdeas.forEach((idea) => {
    if (getResolvedColumnId(idea, columns) !== targetColumnId) {
      nextCardOrder.push(idea.id);
      return;
    }

    while (nextTargetColumnCardIndex < nextTargetColumnCardIds.length) {
      const nextCardId = nextTargetColumnCardIds[nextTargetColumnCardIndex];
      nextTargetColumnCardIndex += 1;
      nextCardOrder.push(nextCardId);

      if (nextCardId === idea.id) {
        break;
      }
    }
  });

  return [...nextCardOrder, ...nextTargetColumnCardIds.slice(nextTargetColumnCardIndex)];
};

export const getColumnIdeasByColumnId = (orderedIdeas: Idea[], columns: BoardColumn[]) => {
  const ideasByColumnId = new Map(columns.map((column) => [column.id, [] as Idea[]]));

  orderedIdeas.forEach((idea) => {
    const columnId = getResolvedColumnId(idea, columns);
    const columnIdeas = ideasByColumnId.get(columnId);
    if (columnIdeas) {
      columnIdeas.push(idea);
    }
  });

  return ideasByColumnId;
};

export const getColumnNavigationIdeas = ({
  ideas,
  columns,
  rankBy,
  randomizeCardPosition,
  randomOrderRef,
}: {
  ideas: Idea[];
  columns: BoardColumn[];
  rankBy: RankBy;
  randomizeCardPosition: boolean;
  randomOrderRef?: RandomOrderRef;
}) => {
  const orderedIdeas = getOrderedBoardIdeas(
    ideas,
    rankBy,
    randomizeCardPosition,
    randomOrderRef
  );
  const ideasByColumnId = getColumnIdeasByColumnId(orderedIdeas, columns);

  return columns.flatMap((column) => ideasByColumnId.get(column.id) ?? []);
};

export const getGridAdjacentIdea = ({
  ideas,
  activeIdeaId,
  direction,
}: {
  ideas: Idea[];
  activeIdeaId: string;
  direction: -1 | 1;
}) => {
  const currentIndex = ideas.findIndex((idea) => idea.id === activeIdeaId);

  if (currentIndex === -1) {
    return null;
  }

  if (ideas.length === 0) {
    return null;
  }

  const nextIndex = (currentIndex + direction + ideas.length) % ideas.length;
  return ideas[nextIndex] ?? null;
};
